import RootStore from 'stores/RootStore'
import CommandError from 'error/commandError'
import DependencyCollectError from 'error/dependencyCollectError'
import DependencyTimeoutError from 'error/dependencyTimeoutError'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { logExecutionTime } from 'utils/performance'
import { rejectAfter, wait } from 'utils/promise'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { UrlParams } from 'utils/url'
import { StartupCommand } from './command/command.interface'

const collectDependenciesConfig = {
  maxRetries: 100,
  waitTime: 200
}

export default class StartupCommandPool {
  private rootStore: RootStore
  private urlParams: UrlParams
  private commands: Record<string, Promise<unknown>> = {}

  constructor(rootStore: RootStore, urlParams: UrlParams) {
    this.rootStore = rootStore
    this.urlParams = urlParams
  }

  private getDependencyStatuses = (dependencies: string[]) =>
    dependencies.reduce(
      (previous, current) => ({
        ...previous,
        [current]: this.commands[current] !== undefined
      }),
      {}
    )

  private hasCollectedDependencies = (dependencies: string[]) =>
    Object.keys(this.commands).filter((commandName) =>
      dependencies.includes(commandName)
    ).length === dependencies.length

  private collectDependencies = async (
    commandName: string,
    dependencies: string[]
  ) => {
    let checkCount = 0
    while (!this.hasCollectedDependencies(dependencies)) {
      // eslint-disable-next-line no-await-in-loop
      await wait(collectDependenciesConfig.waitTime)

      checkCount += 1

      if (checkCount >= collectDependenciesConfig.maxRetries) {
        return Promise.reject(
          new DependencyCollectError(
            commandName,
            this.getDependencyStatuses(dependencies)
          )
        )
      }
    }

    return Promise.resolve()
  }

  private waitForDependencies = async ({
    name,
    dependencies,
    timeout
  }: StartupCommand) => {
    if (dependencies.length === 0) {
      return Promise.resolve()
    }

    await this.collectDependencies(name, dependencies)

    return Promise.race([
      Promise.allSettled(
        dependencies.map((dependency) => this.commands[dependency])
      ),
      rejectAfter(
        timeout,
        () =>
          new DependencyTimeoutError(
            name,
            this.getDependencyStatuses(dependencies),
            timeout
          )
      )
    ])
  }

  private executeCommand = async ({ name, execute }: StartupCommand) => {
    const commandInPool = this.commands[name]

    if (commandInPool) {
      logAndTrackError(new CommandError(name, 'Command was already executed.'))

      return commandInPool
    }

    const promise = execute(this.rootStore, this.urlParams)

    this.commands[name] = promise

    return promise
  }

  run = async (command: StartupCommand) => {
    try {
      await this.waitForDependencies(command)

      await logExecutionTime(
        command.name,
        () => this.executeCommand(command),
        1000
      )
    } catch (error) {
      logAndTrackError(error as Error)

      if (error instanceof DependencyTimeoutError) {
        trackInAmplitude(AmplitudeEvent.LogDependencyTimeout, {
          command: command.name,
          dependencies: command.dependencies.join(',')
        })
      }
    }
  }
}
